import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Alert,
    Box,
    Snackbar,
} from '@mui/material';
import usePositionSizingAttributes from '../../hooks/usePositionSizingAttributes';
import PositionSizingAttributeTargetsTable from './PositionSizingAttributeTargetsTable';
import PositionSizingAttributeSummaryTable from './PositionSizingAttributeSummaryTable';
import {
    WORKSPACE_DIMENSIONS,
    workspaceEqualSplitSx,
} from '../../styles/workspaceChromeStyles';

export default function PositionSizingAttributeWorkspace({
    defaultTargetVolPct,
    defaultTargetVolDollar,
    defaultScaler,
    workspaceBusy,
    setPosSizingData,
    posSizingPublicationBaseRef,
    onPosSizingSaved,
    onTargetRecalculationStateChange,
    hydratedWorkspace = null,
    active = true,
}) {
    const handlePublished = useCallback((result) => {
        setPosSizingData?.(result.pos_sizing || []);
        if (posSizingPublicationBaseRef) {
            posSizingPublicationBaseRef.current = result.publication_base;
        }
        onPosSizingSaved?.({ waitForAuthoritativeStatus: false });
    }, [onPosSizingSaved, posSizingPublicationBaseRef, setPosSizingData]);
    const attributes = usePositionSizingAttributes({
        defaultTargetVolPct,
        defaultTargetVolDollar,
        defaultScaler,
        hydratedWorkspace,
        workspaceBusy,
        active,
        onPublished: handlePublished,
        onTargetRecalculationStateChange,
    });

    if (
        !attributes.capabilityLoading
        && attributes.attributeState
        && !attributes.hasAttributeOptions
        && attributes.attributeState.calculation_admission.can_calculate
    ) {
        return (
            <Alert severity="info">
                Add an attribute column to the Watchlist to use Position Sizing attribute breakdowns.
            </Alert>
        );
    }

    const tableReadOnly = Boolean(
        !attributes.calculationAdmitted || attributes.capabilityLoading,
    );
    const tableLoading = Boolean(attributes.capabilityLoading || workspaceBusy);
    return (
        <>
            <Box
                data-testid="position-sizing-attribute-workspace"
                sx={workspaceEqualSplitSx}
            >
                <PositionSizingAttributeTargetsTable
                    state={attributes.attributeState}
                    viewTargetKey={attributes.viewTargetKey || ''}
                    onViewTargetChange={attributes.selectTarget}
                    rows={attributes.targetRows}
                    savingSettingKey={attributes.savingSettingKey}
                    publishing={attributes.publishing}
                    loading={tableLoading}
                    readOnly={tableReadOnly}
                    allowReadOnlySelection={!attributes.calculationAdmitted}
                    onSaveSetting={attributes.saveSetting}
                    height={WORKSPACE_DIMENSIONS.attributeTableHeight}
                />
                <PositionSizingAttributeSummaryTable
                    options={attributes.summaryOptions}
                    selectedSummary={attributes.selectedSummary}
                    summaryKey={attributes.summaryKey || ''}
                    onSummaryChange={attributes.selectSummary}
                    rows={attributes.summaryRows}
                    loading={tableLoading}
                    height={WORKSPACE_DIMENSIONS.attributeTableHeight}
                />
            </Box>
            <Snackbar
                open={Boolean(attributes.error)}
                autoHideDuration={7000}
                onClose={attributes.clearError}
            >
                <Alert severity="error" onClose={attributes.clearError}>
                    {attributes.error}
                </Alert>
            </Snackbar>
        </>
    );
}

PositionSizingAttributeWorkspace.propTypes = {
    // The member's starting values; absent until they load.
    defaultTargetVolPct: PropTypes.number,
    defaultTargetVolDollar: PropTypes.number,
    defaultScaler: PropTypes.number,
    workspaceBusy: PropTypes.bool,
    setPosSizingData: PropTypes.func,
    posSizingPublicationBaseRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
    onPosSizingSaved: PropTypes.func,
    onTargetRecalculationStateChange: PropTypes.func,
    hydratedWorkspace: PropTypes.shape({
        key: PropTypes.string.isRequired,
        capability: PropTypes.object.isRequired,
        state: PropTypes.object.isRequired,
        calculation: PropTypes.object.isRequired,
    }),
    active: PropTypes.bool,
};
